import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { GameFont } from "../../common/GameFont";
import { gsap } from "gsap";
import { StringFormatExtention } from "../../../../extensions/StringFormatExtention";
import { Event } from "../../../../shared/event-system/Event";
import { IRenterPolicyModel } from "../../common/models/Renters/IRenterPolicyModel";
import { nameof } from "bindable-data";
import { ButtonWithScaleAnimation } from "../../common/components/ButtonWithScaleAnimation";
import { SoundsResourcePackageWrapper } from "../../common/resources/SoundsResourcePackageWrapper";
import { TextWithAdaptiveTime } from "../../common/components/TextWithAdaptiveTime";
import { LobbyButtonsTextStyle } from "./LobbyButtonsTextStyle";
import { GameResourcePackage } from "../resources/GameResourcePackage";

export class RenterButton extends ButtonWithScaleAnimation {
    private static readonly ENDS_TEXT = "ENDS IN\n";

    public readonly onButtonClick: Event<void>;

    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly animationTimeline: gsap.core.Timeline;

    private info: Sprite;
    private renterProgressText: Text;
    private renterTimerText: TextWithAdaptiveTime;

    public constructor(
        commonResources: CommonResourcesPackage,
        gameResources: GameResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        renterPolicyModel: IRenterPolicyModel
    ) {
        super(gameResources.assets_views_game_ui_renters_button_renter_quest_icon_png);
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.renterPolicyModel = renterPolicyModel;
        this.onButtonClick = new Event();
        this.animationTimeline = new gsap.core.Timeline({ paused: true });
        this.build();
        this.onUp.add(this.clickHandle, this);
        this.renterPolicyModel.propertyChanged.add(this.onRenterPolicyModelPropertyChanged, this);
        this.onRenterPolicyModelPropertyChanged(this.renterPolicyModel, nameof(this.renterPolicyModel, "endsInTime"));
        this.onRenterPolicyModelPropertyChanged(this.renterPolicyModel, nameof(this.renterPolicyModel, "cycle"));
        this.onRenterPolicyModelPropertyChanged(this.renterPolicyModel, nameof(this.renterPolicyModel, "isLowTime"));
    }

    private async onRenterPolicyModelPropertyChanged(model: IRenterPolicyModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "endsInTime"):
                this.updateTime(model.endsInTime.value || 0);
                break;
            case nameof(model, "cycle"):
                this.updateProgress(model.cycle.value?.upgradePoints || 0, model.configuration.value?.requiredUpgradePoints || 0);
                break;
            case nameof(model, "isLowTime"):
                this.renterTimerText.style.fill = model.isLowTime.value ? "#FF0000" : "#253480";
                break;
        }
    }

    private build(): void {
        this.info = new Sprite(this.commonResources.assets_common_ui_info_icon_png);
        this.info.position.set(105, -11);
        this.addChild(this.info);

        this.renterProgressText = new Text("", new ProgressStyle());
        this.renterProgressText.position.set(this.defaultView.width * 0.5 - this.renterProgressText.width * 0.5 + 13, this.defaultView.height - this.renterProgressText.height - 21);
        this.addChild(this.renterProgressText);

        this.renterTimerText = new TextWithAdaptiveTime(0, RenterButton.ENDS_TEXT, "", new LobbyButtonsTextStyle());
        this.renterTimerText.position.set(this.defaultView.width * 0.5 - this.renterTimerText.width * 0.5 + 13, this.defaultView.height + 5);
        this.renterTimerText.eventMode = "static";
        this.addChild(this.renterTimerText);
    }

    private clickHandle(): void {
        this.soundResources.assets_sounds_Choose_Card_mp3.play();
        this.onButtonClick.emit();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.animationTimeline.kill();
        this.onUp.remove(this.clickHandle, this);
        this.renterPolicyModel.propertyChanged.remove(this.onRenterPolicyModelPropertyChanged, this);
        super.destroy(options);
    }

    private updateProgress(currentPoints: number, requiredUpgradePoints: number): void {
        this.renterProgressText.text = `${StringFormatExtention.abbreviatedFormat(currentPoints)}/${StringFormatExtention.abbreviatedFormat(requiredUpgradePoints)}`;
        this.renterProgressText.position.set(this.defaultView.width * 0.5 - this.renterProgressText.width * 0.5 + 13, this.defaultView.height - this.renterProgressText.height - 21);
    }

    private updateTime(timeMs: number): void {
        this.renterTimerText.updateTime(timeMs);
        this.renterTimerText.position.set(this.defaultView.width * 0.5 - this.renterTimerText.width * 0.5 + 13, this.defaultView.height + 5);
    }
}

class ProgressStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 24,
            lineJoin: "round"
        });
    }
}
